/**
 * Captures browser-tab audio mixed with the user's microphone.
 *
 * getDisplayMedia MUST request video:true — Chrome and Edge refuse
 * audio-only captures. The video track is stopped immediately after
 * the picker resolves; only the audio track is used.
 *
 * Works in Chrome ≥ 74 and Edge ≥ 79. Firefox exposes getDisplayMedia
 * but may not share tab audio; a runtime check via isSupported() is
 * sufficient — users on Firefox will see the unsupported message.
 */
export class VideoCallRecorder {
  static isSupported(): boolean {
    return typeof navigator?.mediaDevices?.getDisplayMedia === "function";
  }

  private tabStream?: MediaStream;
  private micStream?: MediaStream;
  private audioCtx?: AudioContext;
  private mediaRecorder?: MediaRecorder;
  private recordedBlobs: Blob[] = [];

  /**
   * Open the native screen/tab picker, mix tab audio with mic, and start
   * recording. Throws if the user cancels the picker or denies mic access;
   * whatever was already acquired (the shared tab) is released first.
   */
  async start(): Promise<void> {
    try {
      await this.acquireAndRecord();
    } catch (err) {
      this.releaseMedia();
      throw err;
    }
  }

  private async acquireAndRecord(): Promise<void> {
    this.tabStream = await navigator.mediaDevices.getDisplayMedia({
      video: true,
      audio: true,
    });

    // Video tracks are not needed — stop them immediately to avoid the
    // "sharing your screen" indicator outlasting the recording.
    this.tabStream.getVideoTracks().forEach((t) => t.stop());

    this.micStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
      },
    });

    this.audioCtx = new AudioContext();
    const dest = this.audioCtx.createMediaStreamDestination();

    if (this.tabStream.getAudioTracks().length > 0) {
      this.audioCtx.createMediaStreamSource(this.tabStream).connect(dest);
    }
    this.audioCtx.createMediaStreamSource(this.micStream).connect(dest);

    const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
      ? "audio/webm;codecs=opus"
      : MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : "";

    this.mediaRecorder = mimeType
      ? new MediaRecorder(dest.stream, { mimeType })
      : new MediaRecorder(dest.stream);

    this.recordedBlobs = [];
    this.mediaRecorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) this.recordedBlobs.push(e.data);
    };
    this.mediaRecorder.start(1000);
  }

  pause(): void {
    if (this.mediaRecorder?.state === "recording") this.mediaRecorder.pause();
  }

  resume(): void {
    if (this.mediaRecorder?.state === "paused") this.mediaRecorder.resume();
  }

  isPaused(): boolean {
    return this.mediaRecorder?.state === "paused";
  }

  isActive(): boolean {
    return (
      this.mediaRecorder?.state === "recording" ||
      this.mediaRecorder?.state === "paused"
    );
  }

  /** Stop the tab and mic tracks and close the audio graph. */
  private releaseMedia(): void {
    this.tabStream?.getTracks().forEach((t) => t.stop());
    this.micStream?.getTracks().forEach((t) => t.stop());
    if (this.audioCtx && this.audioCtx.state !== "closed") {
      this.audioCtx.close().catch(() => {});
    }
    this.audioCtx = undefined;
  }

  stop(): Promise<void> {
    this.releaseMedia();

    return new Promise<void>((resolve) => {
      if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
        this.mediaRecorder.addEventListener("stop", () => resolve(), {
          once: true,
        });
        this.mediaRecorder.stop();
      } else {
        resolve();
      }
    });
  }

  getRecordedAudio(): Blob {
    return new Blob(this.recordedBlobs, {
      type: this.mediaRecorder?.mimeType || "audio/webm",
    });
  }
}
